import { useEffect, useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import type { useRorosAgentUpdate } from '@/hooks/roros/agents';
import type { RorosAgentDefinitionDTO } from '@/services/roros/ai-operators.service';

/**
 * Static list of domain tool grants offered in the AI-operator editor.
 * Labels are resolved lazily so the active locale is honoured at render time.
 */
export const DOMAIN_TOOLS = [
  { key: 'get_work_items', label: () => t('roros.operators.tool_get_work_items') },
  { key: 'create_work_item', label: () => t('roros.operators.tool_create_work_item') },
  { key: 'get_outcomes', label: () => t('roros.operators.tool_get_outcomes') },
  { key: 'get_decisions', label: () => t('roros.operators.tool_get_decisions') },
  { key: 'get_goals', label: () => t('roros.operators.tool_get_goals') },
];

interface UseActorToolGrantsParams {
  isOpen: boolean;
  agentDefinition?: RorosAgentDefinitionDTO | null;
  /** Shared agent-update mutation owned by the editor (single instance). */
  updateAgentMutation: ReturnType<typeof useRorosAgentUpdate>;
}

/**
 * Owns the AI-operator "tool grants" concern: the allowed-tools selection
 * state, initialisation from the agent definition on open, the per-tool
 * toggle, and the save handler. Behaviour-identical extraction from
 * UnifiedActorEditor (M10f decomposition).
 */
export function useActorToolGrants({
  isOpen,
  agentDefinition,
  updateAgentMutation,
}: UseActorToolGrantsParams) {
  const [allowedTools, setAllowedTools] = useState<string[]>([]);
  const [toolGrantsSaving, setToolGrantsSaving] = useState(false);

  // Initialize allowedTools from agentDefinition on open
  useEffect(() => {
    if (isOpen && agentDefinition) {
      setAllowedTools(agentDefinition.allowedTools ?? []);
    }
  }, [isOpen, agentDefinition?.id]);

  const handleToolToggle = (key: string, checked: boolean) => {
    setAllowedTools((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const handleToolGrantsSave = async () => {
    if (!agentDefinition?.id) return;
    setToolGrantsSaving(true);
    try {
      await updateAgentMutation.mutateAsync({
        id: agentDefinition.id,
        payload: { allowed_tools: allowedTools },
      });
      showToast.success(t('roros.operators.tool_grants_saved'));
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to save tool grants'));
    } finally {
      setToolGrantsSaving(false);
    }
  };

  return { allowedTools, toolGrantsSaving, handleToolToggle, handleToolGrantsSave };
}
